<template>
  <div class="language-switcher" ref="containerRef">
    <button
      class="globe-button"
      @click="toggleMenu"
      :aria-expanded="isMenuOpen"
      aria-haspopup="true"
      :title="t('common.changeLanguage') || 'Change language'"
    >
      <span class="globe-icon">🌐</span>
      <span class="language-badge">{{ currentLocale.toUpperCase() }}</span>
    </button>

    <Transition name="dropdown">
      <div v-if="isMenuOpen" class="language-menu">
        <button
          v-for="option in localeOptions"
          :key="option.value"
          class="language-menu-item"
          :class="{ active: option.value === currentLocale }"
          @click="selectLanguage(option.value)"
        >
          <span class="menu-flag">{{ option.flag }}</span>
          <span class="menu-label">{{ option.name }}</span>
          <span v-if="option.value === currentLocale" class="menu-check">
            <i class="pi pi-check"></i>
          </span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from '@/composables/useI18n'
import type { Locale } from '@/i18n'

const { t, currentLocale, localeOptions, switchLocale } = useI18n()

const isMenuOpen = ref(false)
const containerRef = ref<HTMLElement | null>(null)

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const selectLanguage = async (locale: Locale) => {
  try {
    await switchLocale(locale)
  } catch (error) {}
  isMenuOpen.value = false
}

// Close menu when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    isMenuOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.language-switcher {
  position: relative;
  display: inline-flex;
}

.globe-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: var(--app-radius-md, 0.5rem);
  background: var(--app-surface-hover, #f1f5f9);
  cursor: pointer;
  transition: all 0.2s ease;
}

.globe-button:hover {
  background: var(--app-surface-ground, #e2e8f0);
  transform: scale(1.05);
}

.globe-button:active {
  transform: scale(0.98);
}

.globe-icon {
  font-size: 1.25rem;
  line-height: 1;
  filter: grayscale(0.2);
}

.language-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  min-width: 1.25rem;
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: white;
  background: linear-gradient(
    135deg,
    var(--app-primary-color, #3b82f6) 0%,
    var(--app-primary-hover, #2563eb) 100%
  );
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Dropdown menu */
.language-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 10rem;
  background: var(--app-surface-card, #ffffff);
  border: 1px solid var(--app-border-light, #e2e8f0);
  border-radius: var(--app-radius-md, 0.5rem);
  box-shadow: var(--app-shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  overflow: hidden;
  z-index: 1000;
}

.language-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;
}

.language-menu-item:hover {
  background: var(--app-surface-hover, #f1f5f9);
}

.language-menu-item.active {
  background: var(--app-primary-50, #eff6ff);
}

.menu-flag {
  font-size: 1.125rem;
  line-height: 1;
}

.menu-label {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--app-text-primary, #0f172a);
}

.menu-check {
  color: var(--app-primary-color, #3b82f6);
  font-size: 0.75rem;
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem) scale(0.95);
}

/* Dark mode */
.app-dark .globe-button {
  background: var(--app-surface-hover, #262626);
}

.app-dark .globe-button:hover {
  background: var(--app-surface-ground, #1f1f1f);
}

.app-dark .globe-icon {
  filter: grayscale(0.1) brightness(1.1);
}

.app-dark .language-badge {
  background: linear-gradient(
    135deg,
    var(--app-primary-color, #60a5fa) 0%,
    var(--app-primary-hover, #3b82f6) 100%
  );
  color: #000000;
}

.app-dark .language-menu {
  background: var(--app-surface-card, #1a1a1a);
  border-color: var(--app-border, #262626);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
}

.app-dark .language-menu-item:hover {
  background: var(--app-surface-hover, #262626);
}

.app-dark .language-menu-item.active {
  background: var(--app-primary-900, #1e3a5f);
}

.app-dark .menu-label {
  color: var(--app-text-primary, #fafafa);
}

.app-dark .menu-check {
  color: var(--app-primary-color, #60a5fa);
}
</style>
